import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import apiClient from "../../services/apiClient";
import { GetDecryptEmailformat, GetDecryptReferenceNumberformat } from "../../Components/Common/Common";

const STORAGE_KEY = "dreamMachineState";

const unwrapResponse = (response) => response?.data?.data ?? response?.data ?? response;

const getErrorMessage = (error) =>
  error?.response?.data?.message ||
  error?.response?.data?.error ||
  error?.message ||
  "Something went wrong";

const readPersistedState = () => {
  if (typeof window === "undefined") return {};

  try {
    return JSON.parse(window.localStorage.getItem(STORAGE_KEY)) || {};
  } catch {
    return {};
  }
};

const persisted = readPersistedState();

const initialState = {
  suggestions: {
    data: [],
    loading: false,
    error: null,
  },
  spark: {
    dreamId: persisted?.spark?.dreamId || persisted?.dreamId || null,
    dreamType: persisted?.spark?.dreamType || persisted?.selectedDreamType || null,
    selectedSuggestion: persisted?.spark?.selectedSuggestion || null,
    promptText: persisted?.spark?.promptText || "",
    response: persisted?.spark?.response || null,
    loading: false,
    error: null,
    success: false,
  },
  blueprint: {
    selectedDreamType: persisted?.blueprint?.selectedDreamType || persisted?.selectedDreamType || "Linear",
    data: persisted?.blueprint?.data || persisted?.blueprint || null,
    loading: false,
    error: null,
    success: false,
  },
  vow: {
    data: persisted?.vow?.data || persisted?.vow || null,
    loading: false,
    error: null,
    success: false,
  },
  garden: {
    data: persisted?.garden?.data || persisted?.garden || [],
    loading: false,
    error: null,
    pagination: persisted?.garden?.pagination || null,
  },
  details: {
    byId: persisted?.details?.byId || {},
    current: persisted?.details?.current || persisted?.details || null,
    loading: false,
    error: null,
  },
};

const getReferenceNumber = () => GetDecryptReferenceNumberformat() || localStorage.getItem("reference_number") || "";
const getEmail = () => GetDecryptEmailformat() || localStorage.getItem("email") || "";

export const fetchDreamSuggestions = createAsyncThunk(
  "dreamMachine/fetchDreamSuggestions",
  async (_, { rejectWithValue }) => {
    try {
      const response = await apiClient.get("/ai/api/dream/suggestions");
      return unwrapResponse(response);
    } catch (error) {
      return rejectWithValue(getErrorMessage(error));
    }
  }
);

export const sparkDream = createAsyncThunk(
  "dreamMachine/sparkDream",
  async ({ dreamText, suggestion } = {}, { rejectWithValue }) => {
    try {
      const payload = {
        user_reference_number: getReferenceNumber(),
        user_email:getEmail(),
        raw_input:suggestion?.prompt_text,
        
      };
      const response = await apiClient.post("/ai/api/dream/spark", payload);
      return {
        request: payload,
        response: unwrapResponse(response),
      };
    } catch (error) {
      return rejectWithValue(getErrorMessage(error));
    }
  }
);

export const generateBlueprint = createAsyncThunk(
  "dreamMachine/generateBlueprint",
  async ({ dreamId, selectedDreamType } = {}, { rejectWithValue }) => {
    try {
      const payload = {
        user_reference_number: getReferenceNumber(),
        selected_dream_type: selectedDreamType,
        dream_type: selectedDreamType,
      };
      const response = await apiClient.post(`/ai/api/dream/${dreamId}/blueprint`, payload);
      return {
        selectedDreamType,
        response: unwrapResponse(response),
      };
    } catch (error) {
      return rejectWithValue(getErrorMessage(error));
    }
  }
);

export const submitDreamVow = createAsyncThunk(
  "dreamMachine/submitDreamVow",
  async ({ dreamId, selectedDreamType, vowText } = {}, { rejectWithValue }) => {
    try {
      const payload = {
        user_reference_number: getReferenceNumber(),
        selected_dream_type: selectedDreamType,
        vow_text: vowText,
      };
      const response = await apiClient.post(`/ai/api/dream/${dreamId}/vow`, payload);
      return unwrapResponse(response);
    } catch (error) {
      return rejectWithValue(getErrorMessage(error));
    }
  }
);

export const fetchDreamGarden = createAsyncThunk(
  "dreamMachine/fetchDreamGarden",
  async ({ page, limit, search } = {}, { rejectWithValue }) => {
    try {
      const params = {
        user_reference_number: getReferenceNumber(),
      };
      if (page) params.page = page;
      if (limit) params.limit = limit;
      if (search) params.search = search;

      const response = await apiClient.get("/ai/api/dream/garden", { params });

      return unwrapResponse(response);
    } catch (error) {
      return rejectWithValue(getErrorMessage(error));
    }
  }
);



export const fetchDreamDetails = createAsyncThunk(
  "dreamMachine/fetchDreamDetails",
  async ({ dreamId } = {}, { rejectWithValue }) => {
    try {
      const response = await apiClient.get(`/ai/api/dream/${dreamId}/details`, {
        params: { user_reference_number: getReferenceNumber() },
      });
      return {
        dreamId,
        response: unwrapResponse(response),
      };
    } catch (error) {
      return rejectWithValue(getErrorMessage(error));
    }
  }
);

const extractDreamId = (payload) =>
  payload?.response?.dream_id ||
  payload?.response?.dreamId ||
  payload?.response?.id ||
  payload?.response?.dream?.dream_id ||
  payload?.response?.dream?.id ||
  payload?.response?.data?.dream_id ||
  null;

const extractDreamType = (payload) =>
  payload?.response?.dream_type ||
  payload?.response?.selected_dream_type ||
  payload?.response?.dreamType ||
  payload?.request?.suggestion?.dream_type ||
  payload?.request?.suggestion?.category ||
  null;

const asArray = (value) => {
  if (Array.isArray(value)) return value;
  if (Array.isArray(value?.items)) return value.items;
  if (Array.isArray(value?.dream)) return value.dream;
  if (Array.isArray(value?.suggestions)) return value.suggestions;
  if (Array.isArray(value?.results)) return value.results;
  if (Array.isArray(value?.data)) return value.data;
  return [];
};

const dreamMachineSlice = createSlice({
  name: "dreamMachine",
  initialState,
  reducers: {
    setSelectedDreamType: (state, action) => {
      state.blueprint.selectedDreamType = action.payload;
      state.spark.dreamType = action.payload;
    },
    clearDreamMachineErrors: (state) => {
      state.suggestions.error = null;
      state.spark.error = null;
      state.blueprint.error = null;
      state.vow.error = null;
      state.garden.error = null;
      state.details.error = null;
    },
    resetDreamFlow: (state) => {
      state.spark = initialState.spark;
      state.blueprint = initialState.blueprint;
      state.vow = initialState.vow;
      state.details.current = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchDreamSuggestions.pending, (state) => {
        state.suggestions.loading = true;
        state.suggestions.error = null;
      })
      .addCase(fetchDreamSuggestions.fulfilled, (state, action) => {
        state.suggestions.loading = false;
        state.suggestions.data = asArray(action.payload);
      })
      .addCase(fetchDreamSuggestions.rejected, (state, action) => {
        state.suggestions.loading = false;
        state.suggestions.error = action.payload;
      })
      .addCase(sparkDream.pending, (state) => {
        state.spark.loading = true;
        state.spark.error = null;
        state.spark.success = false;
      })
      .addCase(sparkDream.fulfilled, (state, action) => {
        state.spark.loading = false;
        state.spark.success = true;
        state.spark.dreamId = extractDreamId(action.payload);
        state.spark.dreamType = extractDreamType(action.payload);
        state.spark.selectedSuggestion = action.payload?.request?.suggestion || null;
        state.spark.promptText = action.payload?.request?.dream_text || "";
        state.spark.response = action.payload?.response || null;
      })
      .addCase(sparkDream.rejected, (state, action) => {
        state.spark.loading = false;
        state.spark.error = action.payload;
      })
      .addCase(generateBlueprint.pending, (state, action) => {
        state.blueprint.loading = true;
        state.blueprint.error = null;
        state.blueprint.success = false;
        if (action.meta.arg?.selectedDreamType) {
          state.blueprint.selectedDreamType = action.meta.arg.selectedDreamType;
          state.spark.dreamType = action.meta.arg.selectedDreamType;
        }
      })
      .addCase(generateBlueprint.fulfilled, (state, action) => {
        state.blueprint.loading = false;
        state.blueprint.success = true;
        state.blueprint.selectedDreamType = action.payload?.selectedDreamType || state.blueprint.selectedDreamType;
        state.spark.dreamType = state.blueprint.selectedDreamType;
        state.blueprint.data = action.payload?.response || null;
      })
      .addCase(generateBlueprint.rejected, (state, action) => {
        state.blueprint.loading = false;
        state.blueprint.error = action.payload;
      })
      .addCase(submitDreamVow.pending, (state) => {
        state.vow.loading = true;
        state.vow.error = null;
        state.vow.success = false;
      })
      .addCase(submitDreamVow.fulfilled, (state, action) => {
        state.vow.loading = false;
        state.vow.success = true;
        state.vow.data = action.payload;
      })
      .addCase(submitDreamVow.rejected, (state, action) => {
        state.vow.loading = false;
        state.vow.error = action.payload;
      })
      .addCase(fetchDreamGarden.pending, (state) => {
        state.garden.loading = true;
        state.garden.error = null;
      })
      .addCase(fetchDreamGarden.fulfilled, (state, action) => {
        state.garden.loading = false;
        state.garden.data = action.payload;
        state.garden.pagination = action.payload?.pagination || action.payload?.meta || null;
      })
      .addCase(fetchDreamGarden.rejected, (state, action) => {
        state.garden.loading = false;
        state.garden.error = action.payload;
      })
      .addCase(fetchDreamDetails.pending, (state) => {
        state.details.loading = true;
        state.details.error = null;
      })
      .addCase(fetchDreamDetails.fulfilled, (state, action) => {
        state.details.loading = false;
        state.details.current = action.payload?.response || null;
        if (action.payload?.dreamId) {
          state.details.byId[action.payload.dreamId] = action.payload?.response || null;
        }
      })
      .addCase(fetchDreamDetails.rejected, (state, action) => {
        state.details.loading = false;
        state.details.error = action.payload;
      });
  },
});

export const { clearDreamMachineErrors, resetDreamFlow, setSelectedDreamType } = dreamMachineSlice.actions;
export { STORAGE_KEY as DREAM_MACHINE_STORAGE_KEY };
export default dreamMachineSlice.reducer;
